import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Smartphone, Upload, CheckCircle2, Trash2, Pencil, Loader2, ImagePlus, Info, Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_KEY = 'default';
const MIN_SIZE = 512;

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 30);

/** Reads width/height of a picked image file in the browser. */
const readImageSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight, url });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this image'));
    };
    img.src = url;
  });

/**
 * Re-encodes the picked icon as a 1024 × 1024 PNG (or keeps it smaller when
 * it already is), so uploads stay well under the server's request-size limit.
 */
const ICON_SIZE = 1024;
const normaliseIcon = (file, width) =>
  new Promise((resolve) => {
    if (width <= ICON_SIZE && file.size <= 900 * 1024) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = Math.min(ICON_SIZE, img.naturalWidth);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) return resolve(file);
        resolve(new File([blob], file.name.replace(/\.png$/i, '') + '.png', { type: 'image/png' }));
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

/** Phone-style preview of a launcher icon. */
const IconPreview = ({ src, size = 64, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div
      className="overflow-hidden bg-white shadow-sm ring-1 ring-black/5 flex items-center justify-center"
      style={{ width: size, height: size, borderRadius: size * 0.23 }}
    >
      {src ? (
        <img src={src} alt={label || 'App icon'} className="h-full w-full object-cover" />
      ) : (
        <Smartphone className="text-gray-300" size={size * 0.45} />
      )}
    </div>
    {label && <span className="text-[11px] text-gray-600 max-w-[80px] truncate">{label}</span>}
  </div>
);

const AppIcon = () => {
  const [data, setData] = useState({ activeKey: DEFAULT_KEY, icons: [], canEdit: false });
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { url, width, height }
  const [name, setName] = useState('');
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  // Rename dialog
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/app-icons`);
      setData({
        activeKey: res.data?.activeKey || DEFAULT_KEY,
        icons: res.data?.icons || [],
        canEdit: !!res.data?.canEdit,
        updatedAt: res.data?.updatedAt,
        updatedByName: res.data?.updatedByName
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load app icons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const applyResponse = (res) => {
    setData((d) => ({
      ...d,
      activeKey: res.data?.activeKey ?? d.activeKey,
      icons: res.data?.icons ?? d.icons,
      updatedAt: res.data?.updatedAt ?? d.updatedAt,
      updatedByName: res.data?.updatedByName ?? d.updatedByName
    }));
  };

  const openUpload = () => {
    setFile(null);
    setPreview(null);
    setName('');
    setFileError('');
    setUploadOpen(true);
  };

  const pickFile = async (f) => {
    setFileError('');
    setFile(null);
    setPreview(null);
    if (!f) return;
    if (f.type !== 'image/png') {
      setFileError('Upload a PNG image');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setFileError('Icon file is too large (max 20 MB before resizing)');
      return;
    }
    try {
      const size = await readImageSize(f);
      if (size.width !== size.height) {
        URL.revokeObjectURL(size.url);
        setFileError(`Icon must be square (this one is ${size.width} × ${size.height})`);
        return;
      }
      if (size.width < MIN_SIZE) {
        URL.revokeObjectURL(size.url);
        setFileError(`Icon must be at least ${MIN_SIZE} × ${MIN_SIZE} (1024 × 1024 recommended)`);
        return;
      }
      const ready = await normaliseIcon(f, size.width);
      if (ready.size > 5 * 1024 * 1024) {
        URL.revokeObjectURL(size.url);
        setFileError('Icon must be smaller than 5 MB — export it as a 1024 × 1024 PNG');
        return;
      }
      setFile(ready);
      setPreview({ ...size, width: Math.min(size.width, ICON_SIZE), height: Math.min(size.height, ICON_SIZE), bytes: ready.size });
      if (!name) setName(f.name.replace(/\.png$/i, '').replace(/[-_]+/g, ' ').slice(0, 40));
    } catch (e) {
      setFileError(e.message);
    }
  };

  const doUpload = async () => {
    if (!file) return setFileError('Choose a PNG icon');
    if (!name.trim()) return toast.error('Enter a name for this icon');
    if (!slugify(name)) return toast.error('Name must contain letters');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      const res = await axios.post(`${API}/app-icons`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      applyResponse(res);
      toast.success('Icon uploaded — include it in the next app release to use it');
      setUploadOpen(false);
    } catch (e) {
      const status = e.response?.status;
      toast.error(
        status === 413
          ? 'The server rejected the file as too large (413). Ask the server admin to raise the upload limit (nginx client_max_body_size), or upload a smaller PNG.'
          : e.response?.data?.detail || 'Upload failed'
      );
    } finally {
      setUploading(false);
    }
  };

  const setActive = async (key) => {
    if (key === data.activeKey) return;
    const label = key === DEFAULT_KEY ? 'the default icon' : `"${data.icons.find((i) => i.key === key)?.name}"`;
    if (!window.confirm(`Make ${label} the customer app icon?\n\nPhones switch the next time the app is opened and closed. Users on an app version that doesn't include this icon keep their current icon until they update.`)) return;
    setBusyKey(key);
    try {
      const res = await axios.patch(`${API}/app-icons/active`, { key });
      applyResponse(res);
      toast.success(res.data?.message || 'App icon updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update the app icon');
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (icon) => {
    if (!window.confirm(`Delete "${icon.name}"?\n\nApp versions that already include it keep it, but it can no longer be selected here.`)) return;
    setBusyKey(icon.key);
    try {
      const res = await axios.delete(`${API}/app-icons/${icon.key}`);
      applyResponse(res);
      toast.success('Icon deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyKey(null);
    }
  };

  const saveRename = async () => {
    if (!renameValue.trim()) return toast.error('Enter a name');
    setBusyKey(renaming.key);
    try {
      const res = await axios.put(`${API}/app-icons/${renaming.key}`, { name: renameValue.trim() });
      applyResponse(res);
      toast.success('Icon renamed');
      setRenaming(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename');
    } finally {
      setBusyKey(null);
    }
  };

  const active = data.activeKey === DEFAULT_KEY ? null : data.icons.find((i) => i.key === data.activeKey);
  const tiles = [{ key: DEFAULT_KEY, name: 'Default', imageUrl: null, builtIn: true }, ...data.icons];

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="app-icon-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center">
            <Smartphone className="text-orange-600" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Customer App Icon</h1>
            <p className="text-sm text-gray-500">Choose the home-screen icon of the customer app.</p>
          </div>
        </div>
        {data.canEdit ? (
          <Button onClick={openUpload} style={{ backgroundColor: '#C2410C' }} data-testid="app-icon-upload">
            <Upload size={16} className="mr-2" /> Upload icon
          </Button>
        ) : (
          !loading && (
            <Badge className="border-0 bg-gray-100 text-gray-600 w-fit"><Lock size={12} className="mr-1" /> Only Super Admin can make changes</Badge>
          )
        )}
      </div>

      {/* Current */}
      <Card className="shadow-sm">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 px-8 py-6 flex gap-6">
            <IconPreview src={active?.imageUrl} size={64} label={active ? active.name : 'Default'} />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">Active icon</div>
            <div className="text-lg font-semibold">{active ? active.name : 'Default (built into the app)'}</div>
            {data.updatedAt && (
              <div className="text-xs text-gray-500 mt-1">
                Changed {format(new Date(data.updatedAt), 'dd MMM yyyy, h:mm a')}{data.updatedByName ? ` by ${data.updatedByName}` : ''}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <Info size={18} className="mt-0.5 shrink-0" />
        <div className="space-y-1">
          <div className="font-medium">How the icon reaches phones</div>
          <ol className="list-decimal pl-5 space-y-0.5">
            <li>Upload the icon here (square PNG, 1024 × 1024).</li>
            <li>The developer runs <code className="rounded bg-white/70 px-1">dart run tool/sync_app_icons.dart</code> in the customer app and publishes the update. Android and iOS only allow icons that are inside the installed app.</li>
            <li>After that, switch between all included icons here any time — no new release needed. Phones change icon the next time the app is opened and closed. iOS shows a short system message when the icon changes.</li>
          </ol>
        </div>
      </div>

      {/* Icons */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-48 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {tiles.map((icon) => {
            const isActive = data.activeKey === icon.key;
            const busy = busyKey === icon.key;
            return (
              <Card
                key={icon.key}
                className={`shadow-sm transition-colors ${isActive ? 'ring-2 ring-orange-500' : ''}`}
                data-testid={`app-icon-tile-${icon.key}`}
              >
                <CardContent className="p-4 flex flex-col items-center text-center gap-3">
                  <div className="relative">
                    <IconPreview src={icon.imageUrl} size={84} />
                    {isActive && (
                      <CheckCircle2 size={22} className="absolute -right-2 -top-2 text-orange-600 bg-white rounded-full" />
                    )}
                  </div>
                  <div className="min-w-0 w-full">
                    <div className="font-medium truncate" title={icon.name}>{icon.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {icon.builtIn ? 'Built into the app' : `key: ${icon.key}`}
                    </div>
                  </div>
                  {data.canEdit && (
                    <div className="flex w-full items-center gap-1">
                      <Button
                        size="sm"
                        className="flex-1"
                        variant={isActive ? 'outline' : 'default'}
                        disabled={isActive || busy}
                        style={isActive ? undefined : { backgroundColor: '#C2410C' }}
                        onClick={() => setActive(icon.key)}
                      >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : isActive ? 'Active' : 'Make active'}
                      </Button>
                      {!icon.builtIn && (
                        <>
                          <Button size="sm" variant="ghost" title="Rename" disabled={busy}
                                  onClick={() => { setRenaming(icon); setRenameValue(icon.name); }}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" title="Delete"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={busy || isActive} onClick={() => remove(icon)}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => !uploading && setUploadOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload app icon</DialogTitle>
            <DialogDescription>Square PNG, at least 512 × 512 (1024 × 1024 recommended), up to 5 MB. Keep important artwork inside the centre 80% — some phones round the corners.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-300 p-6 hover:border-orange-400 hover:bg-orange-50/40 transition-colors"
            >
              {preview ? (
                <div className="flex items-end gap-5">
                  <IconPreview src={preview.url} size={72} label="Square" />
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="h-[72px] w-[72px] rounded-full overflow-hidden bg-white ring-1 ring-black/5">
                      <img src={preview.url} alt="Round preview" className="h-full w-full object-cover" />
                    </div>
                    <span className="text-[11px] text-gray-600">Round</span>
                  </div>
                </div>
              ) : (
                <ImagePlus className="text-gray-400" size={32} />
              )}
              <span className="text-sm font-medium">{file ? `${file.name} • ${preview?.width} × ${preview?.height} • ${Math.max(1, Math.round((preview?.bytes || file.size) / 1024))} KB` : 'Choose a PNG file'}</span>
            </button>
            <input ref={fileInput} type="file" accept="image/png" className="hidden" data-testid="app-icon-file"
                   onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
            {fileError && <p className="text-sm text-red-600">{fileError}</p>}

            <div>
              <Label>Name</Label>
              <Input className="mt-1.5" maxLength={40} value={name} placeholder="e.g. Diwali 2026"
                     onChange={(e) => setName(e.target.value)} />
              {name && slugify(name) && (
                <p className="text-xs text-gray-500 mt-1">Key: <code>{slugify(name)}</code> (used inside the app, can't be changed later)</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={uploading} onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button disabled={uploading || !file} onClick={doUpload} style={{ backgroundColor: '#C2410C' }} data-testid="app-icon-upload-submit">
                {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename icon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input maxLength={40} value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button onClick={saveRename} disabled={busyKey === renaming?.key} style={{ backgroundColor: '#C2410C' }}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppIcon;
